/**
 * useAsanaExportState — manages Asana-specific selection state for export.
 *
 * Handles:
 * - Workspace selection
 * - Project selection (cascade: loads when workspace changes)
 * - Data loading callback for useExportOAuth
 */
import { useState, useCallback, useEffect } from 'react';
import { toUserFacingMessage } from '../../../userFacingError.js';
export function useAsanaExportState(runtime) {
    const { api: integrationsApi, debug } = runtime;
    const [asanaWorkspaces, setAsanaWorkspaces] = useState([]);
    const [selectedWorkspaceGid, setSelectedWorkspaceGid] = useState('');
    const [asanaProjects, setAsanaProjects] = useState([]);
    const [selectedProjectGid, setSelectedProjectGid] = useState('');
    const [projectsLoading, setProjectsLoading] = useState(false);
    // A failed projects fetch used to clear the projects and say nothing, leaving
    // an empty Project select and a permanently disabled Export button (EP-187,
    // the sibling EP-099's fix missed).
    const [projectsError, setProjectsError] = useState(null);
    const [projectsReloadNonce, setProjectsReloadNonce] = useState(0);
    // The workspace `asanaProjects` was fetched for, '' while a fetch is in
    // flight, so `projectsEmpty` means a fetch for THIS workspace landed empty,
    // never the render that selects it before the fetch effect starts.
    const [projectsWorkspaceGid, setProjectsWorkspaceGid] = useState('');
    /** Callback for useExportOAuth to populate Asana data */
    const onAsanaDataLoaded = useCallback((workspaces) => {
        debug.log('Asana workspaces loaded:', workspaces.length);
        setAsanaWorkspaces(workspaces);
        if (workspaces.length > 0) {
            setSelectedWorkspaceGid(workspaces[0].gid);
        }
    }, [runtime]);
    /** Load projects when selected workspace changes */
    useEffect(() => {
        if (!selectedWorkspaceGid) {
            setAsanaProjects([]);
            setSelectedProjectGid('');
            setProjectsError(null);
            return;
        }
        let cancelled = false;
        setProjectsLoading(true);
        setProjectsError(null);
        setProjectsWorkspaceGid('');
        integrationsApi.getAsanaProjects(selectedWorkspaceGid)
            .then((projects) => {
            if (cancelled)
                return;
            debug.log('Asana projects loaded:', projects.length);
            setAsanaProjects(projects);
            if (projects.length > 0) {
                setSelectedProjectGid(projects[0].gid);
            }
            else {
                setSelectedProjectGid('');
            }
            setProjectsError(null);
            setProjectsWorkspaceGid(selectedWorkspaceGid);
            setProjectsLoading(false);
        })
            .catch((err) => {
            if (cancelled)
                return;
            debug.error('Failed to load projects:', err);
            setAsanaProjects([]);
            setSelectedProjectGid('');
            setProjectsError(toUserFacingMessage(err, "Couldn't load projects for this workspace."));
            setProjectsLoading(false);
        });
        return () => { cancelled = true; };
    }, [selectedWorkspaceGid, projectsReloadNonce, runtime]);
    /** Refetch the projects for the current workspace after a failure. */
    const retryLoadProjects = useCallback(() => {
        setProjectsReloadNonce((n) => n + 1);
    }, [runtime]);
    /** Reset Asana-specific state */
    const resetAsanaState = useCallback(() => {
        debug.log('Resetting Asana state');
        setSelectedWorkspaceGid('');
        setAsanaProjects([]);
        setSelectedProjectGid('');
        setProjectsError(null);
        setProjectsWorkspaceGid('');
    }, [runtime]);
    return {
        asanaWorkspaces,
        selectedWorkspaceGid,
        setSelectedWorkspaceGid,
        asanaProjects,
        selectedProjectGid,
        setSelectedProjectGid,
        projectsLoading,
        projectsError,
        projectsEmpty: !!selectedWorkspaceGid && projectsWorkspaceGid === selectedWorkspaceGid && asanaProjects.length === 0,
        retryLoadProjects,
        onAsanaDataLoaded,
        resetAsanaState,
    };
}
//# sourceMappingURL=useAsanaExportState.js.map